import { PageCanvas } from "@/components/page-canvas";
import { Form, Head, Link, usePage } from "@inertiajs/react";
import { useTranslation } from "react-i18next";
import AppLayout from "@/layouts/app_layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import {
  locationServiceOfferingPath,
  locationServiceOfferingsPath,
  locationServicesPath,
} from "@/routes";
import type { Service, Offering } from "./types";

export default function OfferingForm({
  service,
  offering,
}: {
  service: Pick<Service, "id" | "name" | "archived">;
  offering: Offering | null;
}) {
  const { auth, errors } = usePage().props;
  const { t } = useTranslation();
  if (!auth.location) return null;
  const locationId = auth.location.id;
  return (
    <AppLayout breadcrumbs={[]}>
      <Head title={t("services.configure")} />
      <PageCanvas>
        <div>
          <h1 className="text-page font-display">{t("services.configure")}</h1>
          <p className="text-muted-foreground mt-2 break-words">
            {service.name} · {auth.location.name}
          </p>
        </div>
        {service.archived && <p>{t("services.archived_help")}</p>}
        <Card>
          <CardContent className="pt-6">
            <Form
              key={offering?.revision || "new"}
              action={
                offering
                  ? locationServiceOfferingPath(locationId, offering.id)
                  : locationServiceOfferingsPath(locationId)
              }
              method={offering ? "patch" : "post"}
            >
              {({ processing }) => (
                <div className="space-y-5">
                  {Object.keys(errors || {}).length > 0 && (
                    <div role="alert" className="text-destructive text-sm">
                      {Object.values(errors).map((error, i) => (
                        <p key={i}>{error}</p>
                      ))}
                    </div>
                  )}
                  <input type="hidden" name="service_id" value={service.id} />
                  {offering && (
                    <input
                      type="hidden"
                      name="revision"
                      value={offering.revision}
                    />
                  )}
                  <div className="grid gap-4 sm:grid-cols-2">
                    {(
                      [
                        "price",
                        "currency",
                        "estimated_minutes",
                        "tax_rate",
                        "tax_label",
                      ] as const
                    ).map((field) => (
                      <div className="space-y-2" key={field}>
                        <Label htmlFor={`offering-${field}`}>
                          {t(
                            field === "price"
                              ? "services.unit_price"
                              : `services.${field}`,
                          )}
                        </Label>
                        <Input
                          id={`offering-${field}`}
                          autoFocus={field === "price"}
                          name={`service_offering[${field}]`}
                          type={
                            [
                              "currency",
                              "tax_label",
                              "price",
                              "tax_rate",
                            ].includes(field)
                              ? "text"
                              : "number"
                          }
                          min={field === "estimated_minutes" ? 1 : 0}
                          step={1}
                          inputMode={
                            field === "price" || field === "tax_rate"
                              ? "decimal"
                              : undefined
                          }
                          defaultValue={
                            offering?.[field] ??
                            (field === "currency"
                              ? auth.location?.currency || ""
                              : field === "tax_rate"
                                ? 0
                                : "")
                          }
                          aria-invalid={!!errors?.[field]}
                          aria-describedby={
                            field === "price" || field === "tax_rate"
                              ? "offering-input-help"
                              : undefined
                          }
                        />
                      </div>
                    ))}
                    <div className="space-y-2">
                      <Label htmlFor="offering-tax_mode">
                        {t("services.tax_mode")}
                      </Label>
                      <Select
                        name="service_offering[tax_mode]"
                        defaultValue={offering?.tax_mode || "exclusive"}
                        items={{
                          inclusive: t("services.inclusive"),
                          exclusive: t("services.exclusive"),
                        }}
                      >
                        <SelectTrigger
                          id="offering-tax_mode"
                          className="w-full"
                          aria-invalid={!!errors?.tax_mode}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="exclusive">
                            {t("services.exclusive")}
                          </SelectItem>
                          <SelectItem value="inclusive">
                            {t("services.inclusive")}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <p
                    id="offering-input-help"
                    className="text-muted-foreground text-sm"
                  >
                    {t("services.amount_help")}
                  </p>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="offering-available"
                      name="service_offering[available]"
                      value="1"
                      uncheckedValue="0"
                      defaultChecked={offering?.available ?? true}
                    />
                    <Label htmlFor="offering-available">
                      {t("services.available")}
                    </Label>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <Button type="submit" disabled={processing}>
                      {t("services.save_offering")}
                    </Button>
                    <Button
                      variant="outline"
                      render={<Link href={locationServicesPath(locationId)} />}
                    >
                      {t("customers.cancel")}
                    </Button>
                  </div>
                </div>
              )}
            </Form>
          </CardContent>
        </Card>
      </PageCanvas>
    </AppLayout>
  );
}
